import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  Mic, 
  MicOff,
  X,
  Heart,
  AlertTriangle,
  Activity,
  Flame,
  Zap,
  Eye
} from 'lucide-react';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';

const AIChatbot = ({ profileId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [quickActions, setQuickActions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchQuickActions();
    // Add welcome message
    setMessages([
      {
        role: 'assistant',
        content: 'Hello! I\'m your AI medical assistant. I can provide first-aid guidance and emergency medical information. How can I help you today?',
        timestamp: new Date()
      }
    ]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchQuickActions = async () => {
    try {
      const response = await api.get('/chatbot/quick-actions');
      if (response.success) {
        setQuickActions(response.quickActions);
      }
    } catch (error) {
      console.error('Failed to fetch quick actions:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.post('/chatbot/chat', {
        message: messageText,
        profileId: profileId,
        conversationHistory: messages.slice(-10) // Send last 10 messages for context
      });

      if (response.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or call emergency services if this is an emergency.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (action) => {
    sendMessage(action.prompt);
  };

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice input is not supported in your browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const stopVoiceInput = () => {
    setIsListening(false);
  };

  const getQuickActionIcon = (iconName) => {
    const icons = {
      '❤️': Heart,
      '🫁': Activity,
      '🩸': Flame,
      '⚡': Zap,
      '🔥': AlertTriangle,
      '😨': Eye
    };
    return icons[iconName] || MessageCircle;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center space-x-2">
          <MessageCircle className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-gray-900">AI Medical Assistant</h3>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Emergency first-aid guidance (not medical advice)
        </p>
      </div>

      {/* Quick Actions */}
      {quickActions.length > 0 && messages.length <= 1 && (
        <div className="bg-white border-b p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Actions:</h4>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((action) => {
              const Icon = getQuickActionIcon(action.icon);
              return (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  className="flex items-center space-x-2 p-2 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-lg">{action.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{action.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-grow overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                message.role === 'user'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-800 border'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              <p className={`text-xs mt-1 ${
                message.role === 'user' ? 'text-red-100' : 'text-gray-400'
              }`}>
                {message.timestamp.toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border rounded-lg px-4 py-2">
              <LoadingSpinner size="small" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white border-t p-4">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <div className="flex-grow relative">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about emergency medical help..."
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={isLoading}
            />
            {isListening && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </div>
          
          <button
            type="button"
            onClick={isListening ? stopVoiceInput : startVoiceInput}
            className={`p-2 rounded-lg transition-colors ${
              isListening
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            disabled={isLoading}
          >
            {isListening ? (
              <MicOff className="h-5 w-5" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </button>
          
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <LoadingSpinner size="small" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </form>
        
        {/* Disclaimer */}
        <p className="text-xs text-gray-500 mt-2 text-center">
          This AI provides first-aid guidance only. Call emergency services for serious conditions.
        </p>
      </div>
    </div>
  );
};

export default AIChatbot;
